import { ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { buttonSize, buttonVariant } from '@models/button-options.enum';
import { AuthService } from '@service/auth.service';
import { Vessel } from 'icreate/common/models/vessel';
import { mimicValidator } from 'icreate/common/validators/mimic/mimic-validator';
import { ImportPDFService } from '@service/canvas/import-pdf.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { RootItem, RootItemType } from 'icreate/common/models/mimic-folder';
import { Mimic } from 'icreate/common/models/mimic';

@Component({
    selector: 'app-new-mimic-dialog',
    templateUrl: './new-mimic-dialog.component.html',
    styleUrls: ['./new-mimic-dialog.component.css'],
    standalone: false,
})
export class NewMimicDialogComponent implements OnInit, OnDestroy {
    readonly BUTTON_SMALL_SIZE = buttonSize.small;
    readonly BUTTON_OUTLINE = buttonVariant.outline;

    readonly GRID_MAX = 200;
    readonly GRID_MIN = 1;

    @Output() onSaveButtonClick = new EventEmitter<{
        name: string;
        userId: string;
        vesselId: string;
        bkcolor: string;
        color: string;
        grid: number;
        allowGrid: boolean;
    }>();

    name: string = '';
    bkcolor: string = '#131B23';
    color: string = '#bccff2';
    grid: number = 1;

    allowGrid: boolean = false;
    loadingPDFViews: Boolean = false;
    titleError: string;

    loadingSubscription: Subscription;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vessel: Vessel;
            rootList: RootItem[];
            mimicFolderId: string;
        },
        private dialogRef: MatDialogRef<NewMimicDialogComponent>,
        private authUserService: AuthService,
        private importPDFService: ImportPDFService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loadingSubscription = this.importPDFService.loadingFinished$.subscribe(() => {
            this.loadingPDFViews = false;
            this.cancelButtonClick();
            this.cdr.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this.loadingSubscription?.unsubscribe();
    }

    saveButtonClick() {
        if (this.titleError) return;
        this.onSaveButtonClick.emit({
            name: this.name,
            userId: this.authUserService.getCurrentUser().oid,
            vesselId: this.data.vessel.id,
            bkcolor: this.bkcolor,
            color: this.color,
            grid: this.grid,
            allowGrid: this.allowGrid,
        });
        this.dialogRef.close(true);
    }

    nameChange(value: string) {
        this.name = value;
        this.titleIsRepeated();
    }

    backgroundColorChange(color: string) {
        this.bkcolor = color;
        this.cdr.detectChanges();
    }

    colorChange(color: string) {
        this.color = color;
        this.cdr.detectChanges();
    }

    cancelButtonClick() {
        this.dialogRef.close(false);
    }

    gridChange(event: { name: string; value: string }) {
        this.grid = Number(event.value);
        this.cdr.detectChanges();
    }

    gridInputButtonClick(event: 'minus' | 'plus') {
        if (event === 'minus' && this.grid > this.GRID_MIN) this.grid -= 1;
        else if (event === 'plus' && this.grid < this.GRID_MAX) this.grid += 1;
        this.cdr.detectChanges();
    }

    allowGridChange(checked: boolean) {
        this.allowGrid = checked;
        this.cdr.detectChanges();
    }

    titleIsRepeated(): void {
        const mimics: Mimic[] =
            this.data.mimicFolderId === null
                ? this.data.rootList.filter(item => item.type === RootItemType.mimic).map(item => item.mimic)
                : (this.data.rootList.find(
                      item => item.type === RootItemType.folder && item.folder?.id === this.data.mimicFolderId
                  )?.folder?.mimicList ?? []);

        if (mimics.length == 0) {
            this.titleError = undefined;
            return;
        }

        const hasError = mimics.some(mimic => mimic.name == this.name);
        this.titleError = hasError ? this.translateService.instant('mimic.create.error.title.repeat') : undefined;
    }

    public get validatorFunction() {
        return mimicValidator;
    }

    onImportPDF() {
        const ele = document.getElementById('viewPDFUpload');
        ele.click();
    }

    async onSetPDF(event) {
        this.loadingPDFViews = true;
        try {
            await this.importPDFService.onSetPDF(event);
        } catch (err) {
            console.error(err);
            this.loadingPDFViews = false;
            this.cancelButtonClick();
            this.cdr.detectChanges();
        } finally {
            event.target.value = '';
        }
    }
}
